import { resolveApiBaseUrl } from '@/utils/resolveApiBaseUrl';

const STORAGE_KEY = 'frontend-diagnostics-navigation';
const MAX_BREADCRUMBS = 30;
const MAX_STACK_LENGTH = 12_000;
const CLIENT_ERROR_ENDPOINT = '/api/app/support/client-error';

type Breadcrumb = {
  at: string;
  path: string;
  action: string;
};

type DiagnosticOptions = {
  reason?: string;
  resourceUrl?: string;
  from?: string;
  to?: string;
  componentStack?: string;
};

const lastReportedAt = new Map<string, number>();

const redact = (value: string) =>
  value
    .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, '[email]')
    .replace(/\b\d{11}\b/g, '[document]')
    .replace(/\b\d{10,}\b/g, '[id]')
    .slice(0, MAX_STACK_LENGTH);

const sanitizePath = (path: string) => {
  const pathname = path.split(/[?#]/, 1)[0] || '/';
  const segments = pathname.split('/');
  return segments
    .map((segment, index) => {
      const previous = segments[index - 1];
      const previousTwo = segments.slice(Math.max(0, index - 2), index).join('/');
      if (
        previous === 'invite' ||
        previousTwo === 'teleattendance/public' ||
        previousTwo === 'onboarding-interviews/public' ||
        previousTwo === 'onboarding-interviews/recording'
      ) {
        return ':token';
      }
      return /^\d+$/.test(segment) || /^[0-9a-f-]{24,}$/i.test(segment) || segment.length > 48
        ? ':id'
        : segment;
    })
    .join('/')
    .slice(0, 300);
};

const sanitizeResourceUrl = (resourceUrl: string) => {
  try {
    const url = new URL(resourceUrl, window.location.href);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return undefined;
    return `${url.origin}${sanitizePath(url.pathname)}`.slice(0, 500);
  } catch {
    return undefined;
  }
};

const readBreadcrumbs = (): Breadcrumb[] => {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(parsed) ? parsed.slice(-MAX_BREADCRUMBS) : [];
  } catch {
    return [];
  }
};

export const recordRouteBreadcrumb = (path: string, action: string) => {
  try {
    const breadcrumbs = readBreadcrumbs();
    const sanitizedPath = sanitizePath(path);
    const previousPath = breadcrumbs[breadcrumbs.length - 1]?.path;
    const previousVisitIndex = breadcrumbs
      .slice(0, -1)
      .map((item) => item.path)
      .lastIndexOf(sanitizedPath);
    breadcrumbs.push({
      at: new Date().toISOString(),
      path: sanitizedPath,
      action: action.slice(0, 32),
    });
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(breadcrumbs.slice(-MAX_BREADCRUMBS)),
    );

    if (action === 'REPLACE' && previousVisitIndex >= 0) {
      const routeChangesSinceVisit = breadcrumbs.slice(previousVisitIndex + 1, -1);
      const uniqueRoutesSinceVisit = new Set(routeChangesSinceVisit.map((item) => item.path));
      const visitTime = Date.parse(breadcrumbs[previousVisitIndex].at);
      if (
        previousPath !== sanitizedPath &&
        uniqueRoutesSinceVisit.size >= 3 &&
        Number.isFinite(visitTime) &&
        Date.now() - visitTime <= 2 * 60_000
      ) {
        reportFrontendDiagnostic(
          'unexpected_route_return',
          new Error('A replace navigation returned to a recently visited screen'),
          {
            from: previousPath,
            to: sanitizedPath,
            reason: `replace_after_${uniqueRoutesSinceVisit.size}_distinct_routes`,
          },
        );
      }
    }
  } catch {
    // Diagnostics must never interfere with application navigation.
  }
};

const readAccessToken = () => {
  try {
    const stored = JSON.parse(localStorage.getItem('auth-storage') || '{}');
    return stored?.state?.token as string | undefined;
  } catch {
    return undefined;
  }
};

const readAppVersion = () =>
  String(import.meta.env.VITE_APP_VERSION || 'unknown').slice(0, 30);

export const reportFrontendDiagnostic = (
  eventName: string,
  error: unknown,
  options: DiagnosticOptions = {},
) => {
  try {
    const token = readAccessToken();
    if (!token) return;

    const diagnosticError =
      error instanceof Error ? error : new Error(String(error ?? 'Unknown error'));
    const signature = `${eventName}:${diagnosticError.message}`;
    const now = Date.now();
    if (now - (lastReportedAt.get(signature) || 0) < 10_000) return;
    if (lastReportedAt.size >= 100) lastReportedAt.clear();
    lastReportedAt.set(signature, now);
    const metadata = {
      source: 'react_client',
      event: eventName.slice(0, 80),
      message: redact(diagnosticError.message || String(error ?? 'Unknown error')).slice(0, 1000),
      stack: redact(diagnosticError.stack || '').slice(0, MAX_STACK_LENGTH),
      current_path: sanitizePath(window.location.pathname),
      resource_url: options.resourceUrl
        ? sanitizeResourceUrl(options.resourceUrl)
        : undefined,
      previous_path: options.from ? sanitizePath(options.from) : undefined,
      target_path: options.to ? sanitizePath(options.to) : undefined,
      navigation_reason: options.reason?.slice(0, 120),
      component_stack: options.componentStack?.slice(0, 4_000),
      app_version: readAppVersion(),
      build_mode: import.meta.env.MODE,
      user_agent: navigator.userAgent.slice(0, 512),
      online: navigator.onLine,
      visibility: document.visibilityState,
      breadcrumbs: readBreadcrumbs(),
    };

    const body = JSON.stringify({
      event: eventName.slice(0, 80),
      message: metadata.message,
      metadata,
    });

    void fetch(`${resolveApiBaseUrl()}${CLIENT_ERROR_ENDPOINT}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'X-Client-Source': 'WEB',
      },
      body,
      keepalive: true,
    }).catch(() => {
      // Reporting is best effort and must not create another application error.
    });
  } catch {
    // Diagnostics must never interfere with application behavior.
  }
};

export const installChunkLoadDiagnostics = () => {
  window.addEventListener('vite:preloadError', (event) => {
    reportFrontendDiagnostic(
      'vite_preload_error',
      (event as unknown as { payload?: unknown }).payload,
    );

    try {
      const reloadKey = 'frontend-diagnostics-chunk-reload-at';
      const lastReloadAt = Number(sessionStorage.getItem(reloadKey) || 0);
      if (Date.now() - lastReloadAt < 30_000) return;

      event.preventDefault();
      sessionStorage.setItem(reloadKey, String(Date.now()));
      window.location.reload();
    } catch {
      // Keep Vite's original load-error behavior if session storage is unavailable.
    }
  });
};

export const installGlobalErrorDiagnostics = () => {
  const handleWindowError = (event: Event) => {
    if (event instanceof ErrorEvent) {
      reportFrontendDiagnostic(
        'window_error',
        event.error || new Error(event.message || 'Uncaught browser error'),
        { reason: `${sanitizePath(event.filename || '')}:${event.lineno || 0}:${event.colno || 0}` },
      );
      return;
    }

    const target = event.target;
    if (target instanceof HTMLScriptElement || target instanceof HTMLLinkElement) {
      const resource = target instanceof HTMLScriptElement ? 'script' : 'stylesheet';
      const resourceUrl =
        target instanceof HTMLScriptElement ? target.src : target.href;
      reportFrontendDiagnostic(
        'resource_load_error',
        new Error(`Failed to load ${resource}`),
        { reason: resource, resourceUrl },
      );
    }
  };
  window.addEventListener('error', handleWindowError, true);

  window.addEventListener('unhandledrejection', (event) => {
    reportFrontendDiagnostic('unhandled_rejection', event.reason);
  });
};
